import { Component, Inject } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FacetDateParts, FacetDescriptor, FacetRange, FacetValue, FacetValues } from 'src/app/core/comparable';

export interface ComparableFilterDialogInput {
  descriptors: FacetDescriptor[];
  /** Selectable values per column, as the server derived them under the current filter. */
  values: { [key: string]: FacetValues };
  facets: { [key: string]: FacetValue[] };
  ranges: { [key: string]: FacetRange };
  dateParts: { [key: string]: FacetDateParts };
}

export interface ComparableFilterDialogOutput {
  facets: { [key: string]: FacetValue[] };
  ranges: { [key: string]: FacetRange };
  dateParts: { [key: string]: FacetDateParts };
}

/** One accordion section: the descriptor plus everything needed to render its controls. */
interface Section {
  descriptor: FacetDescriptor;
  /** Every value on offer, unfiltered by the section's own search box. */
  values: FacetValue[];
  truncated: boolean;
  search: string;
}

/**
 * The comparables filter: one section per column, ticked values plus a From/To for
 * numbers and a month/year for dates — the same controls the valuation comparables table
 * offers.
 *
 * The values come from the server rather than from the rows on screen, so the list this
 * filters can stay paginated. Nothing here derives anything from the comparables.
 */
@Component({
  selector: 'kt-comparable-filter-dialog',
  templateUrl: './comparable-filter-dialog.component.html',
  styleUrls: ['./comparable-filter-dialog.component.scss']
})
export class ComparableFilterDialogComponent {
  sections: Section[] = [];

  /** Ticked values per column while the dialog is open. */
  private selected: { [key: string]: Set<FacetValue> } = {};
  private ranges: { [key: string]: FacetRange } = {};
  private dateParts: { [key: string]: FacetDateParts } = {};

  constructor(
    public dialogRef: MatDialogRef<ComparableFilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ComparableFilterDialogInput
  ) {
    this.sections = (data.descriptors || []).map(descriptor => {
      const values = data.values && data.values[descriptor.key] ? data.values[descriptor.key] : null;
      return {
        descriptor,
        values: values ? values.values : [],
        truncated: values ? values.truncated : false,
        search: ''
      };
    });

    this.sections.forEach(section => {
      const key = section.descriptor.key;
      const ticked = data.facets && data.facets[key] ? data.facets[key] : [];
      // An empty selection means "no restriction", which reads as every box ticked.
      this.selected[key] = new Set(ticked.length > 0 ? ticked : section.values);
      this.ranges[key] = this.copyRange(data.ranges ? data.ranges[key] : null);
      this.dateParts[key] = this.copyDateParts(data.dateParts ? data.dateParts[key] : null);
    });
  }

  trackByKey(index: number, section: Section) {
    return section.descriptor.key;
  }

  trackByIndex(index: number) {
    return index;
  }

  label(section: Section, value: FacetValue): string {
    if (value === null || value === '') {
      return section.descriptor.emptyLabel;
    }
    return String(value);
  }

  /** Values left after the section's own search box. */
  visibleValues(section: Section): FacetValue[] {
    const term = section.search.trim().toLowerCase();
    if (term === '') {
      return section.values;
    }
    return section.values.filter(value => this.label(section, value).toLowerCase().includes(term));
  }

  isTicked(section: Section, value: FacetValue): boolean {
    return this.selected[section.descriptor.key].has(value);
  }

  allTicked(section: Section): boolean {
    return section.values.length > 0
      && this.selected[section.descriptor.key].size === section.values.length;
  }

  onToggleValue(section: Section, value: FacetValue, event: MatCheckboxChange) {
    const set = this.selected[section.descriptor.key];
    if (event.checked) {
      set.add(value);
    } else {
      set.delete(value);
    }
  }

  onToggleAll(section: Section, event: MatCheckboxChange) {
    // Only what the search is currently showing, so "select all" means what it says.
    const visible = this.visibleValues(section);
    const set = this.selected[section.descriptor.key];
    visible.forEach(value => event.checked ? set.add(value) : set.delete(value));
  }

  onSearch(section: Section, term: string) {
    section.search = term || '';
  }

  range(section: Section): FacetRange {
    return this.ranges[section.descriptor.key];
  }

  parts(section: Section): FacetDateParts {
    return this.dateParts[section.descriptor.key];
  }

  onChangeRange(section: Section, bound: 'min' | 'max', raw: string) {
    this.ranges[section.descriptor.key][bound] = this.toNumber(raw);
  }

  onChangeMonth(section: Section, raw: string) {
    const month = this.toNumber(raw);
    this.dateParts[section.descriptor.key].month = month !== null && month >= 1 && month <= 12 ? month : null;
  }

  onChangeYear(section: Section, raw: string) {
    this.dateParts[section.descriptor.key].year = this.toNumber(raw);
  }

  onClose() {
    this.dialogRef.close();
  }

  onSubmit() {
    const facets: { [key: string]: FacetValue[] } = {};
    const ranges: { [key: string]: FacetRange } = {};
    const dateParts: { [key: string]: FacetDateParts } = {};

    this.sections.forEach(section => {
      const key = section.descriptor.key;
      const ticked = Array.from(this.selected[key]);

      // Every value ticked is the same as no restriction; send nothing rather than a
      // list the server would have to match every row against.
      if (ticked.length > 0 && ticked.length < section.values.length) {
        facets[key] = ticked;
      }

      const range = this.ranges[key];
      if (range.min !== null || range.max !== null) {
        ranges[key] = range;
      }

      const parts = this.dateParts[key];
      if (parts.month !== null || parts.year !== null) {
        dateParts[key] = parts;
      }
    });

    this.dialogRef.close({ facets, ranges, dateParts } as ComparableFilterDialogOutput);
  }

  private toNumber(raw: string): number | null {
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return null;
    }
    const parsed = Number(raw);
    return isNaN(parsed) ? null : parsed;
  }

  private copyRange(range: FacetRange | null): FacetRange {
    return {
      min: range && range.min !== undefined ? range.min : null,
      max: range && range.max !== undefined ? range.max : null
    };
  }

  private copyDateParts(parts: FacetDateParts | null): FacetDateParts {
    return {
      month: parts && parts.month !== undefined ? parts.month : null,
      year: parts && parts.year !== undefined ? parts.year : null
    };
  }
}
